import { Action } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { put, takeLatest } from "redux-saga/effects";
import { UserModel } from "services/api/cplauthapi/CPLAuthAPI";
import { ISessionService } from "services/SessionService";
import { container } from "setup/dependency/inversify.config";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { getUserByToken, logout } from "./AuthCRUD";
import { IAuthenticationService } from "services/AuthenticationService";

export interface ActionWithPayload<T> extends Action {
  payload?: T;
}

export const actionTypes = {
  Login: "[Login] Action",
  Logout: "[Logout] Action",
  UserRequested: "[Request User] Action",
  UserLoaded: "[Load User] Auth API",
  SetUser: "[Set User] Action",
  SetToken: "[Set Token] Action",
  UserAndTokensRefreshRequested: "[Refresh User and Tokens] Action",
  SetExternalAccessData: "[Set External Access Data] Action",
};

const initialAuthState: IAuthState = {
  user: undefined,
  token: undefined,
  externalAccess: undefined,
};

export type ExternalAccessData = {
  singleUseToken: string;
  externalAccessToken: string;
  accessPath: string;
};

export interface IAuthState {
  user?: UserModel;
  token?: string;
  externalAccess?: ExternalAccessData;
}

export const reducer = persistReducer(
  { storage, key: "v100-demo1-auth", whitelist: ["user", "token"] },
  (state: IAuthState = initialAuthState, action: ActionWithPayload<IAuthState>) => {
    switch (action.type) {
      case actionTypes.Login: {
        const token = action.payload?.token;
        return { token, user: undefined, externalAccess: undefined };
      }

      case actionTypes.Logout: {
        return initialAuthState;
      }

      case actionTypes.UserLoaded: {
        const user = action.payload?.user;
        return { ...state, user };
      }

      case actionTypes.SetUser: {
        const user = action.payload?.user;
        return { ...state, user };
      }

      case actionTypes.SetToken: {
        const token = action.payload?.token;
        return { ...state, token };
      }

      case actionTypes.SetExternalAccessData: {
        const externalAccessData = action.payload?.externalAccess;
        return { ...state, externalAccess: externalAccessData };
      }

      default:
        return state;
    }
  }
);

export const actions = {
  login: (token: string) => ({ type: actionTypes.Login, payload: { token } }),
  logout: () => ({ type: actionTypes.Logout }),
  requestUser: () => ({ type: actionTypes.UserRequested }),
  setUser: (user: UserModel) => ({ type: actionTypes.SetUser, payload: { user } }),
  setToken: (token: string) => ({ type: actionTypes.SetToken, payload: { token } }),
  refreshUserAndTokens: () => ({ type: actionTypes.UserAndTokensRefreshRequested }),
  setExternalAccessTokens: (externalAccessData: ExternalAccessData) => ({
    type: actionTypes.SetExternalAccessData,
    payload: { externalAccess: externalAccessData },
  }),
};

export function* saga() {
  yield takeLatest(actionTypes.Login, function* loginSaga() {
    yield put(actions.requestUser());
  });

  yield takeLatest(actionTypes.UserRequested, function* userRequested() {
    const sessionService = container.get<ISessionService>(SERVICE_TYPES.SessionService);
    const token: string = yield sessionService.getCPLSessionToken();
    yield put(actions.setToken(token));
    const user: UserModel = yield getUserByToken(token);
    yield put(actions.setUser(user));
  });

  yield takeLatest(actionTypes.UserAndTokensRefreshRequested, function* refreshUserAndTokens() {
    const authenticationService = container.get<IAuthenticationService>(SERVICE_TYPES.AuthenticationService);
    yield authenticationService.getNewSessionTokens();
    yield put(actions.requestUser());
  });

  yield takeLatest(actionTypes.Logout, function* logoutSaga() {
    yield logout();
  });
}
